import { FunctionComponent } from 'react';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import SvgFragment from '../../../../fragments/SvgFragment';
import { campaignMobile } from '../../utils/campaignBreakpoints';

/**
 * Page bar for the campaign leads table.
 *
 *   [20 per page ⌄]  1–20 of 100                      ‹  1  2  …  5  ›
 *
 * Page numbers collapse around the current page once there are more than
 * MAX_PLAIN_PAGES of them, so the bar never wraps on a narrow panel.
 */

export const LEADS_PAGE_SIZE_OPTIONS = [20, 50, 100, 200];

/** Above this many pages the list collapses to first / current / last. */
const MAX_PLAIN_PAGES = 3;

type PageItem = number | 'gap';

/**
 * Exported for tests: the sequence of buttons for a given page/pageCount.
 *
 * Deliberately tight — the design shows `1 2 … 4`, not a long strip of numbers.
 * At most four numbers are ever rendered: the first, the current page with a
 * neighbour where one fits, and the last.
 */
export function leadsPageItems(page: number, pageCount: number): PageItem[] {
  if (pageCount <= MAX_PLAIN_PAGES + 1) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }

  const items: PageItem[] = [1];
  // Near the start show 1 2 … last; near the end 1 … n-1 n; in the middle 1 … p … last.
  let start = Math.max(2, page - (page >= pageCount - 1 ? 1 : 0));
  let end = Math.min(pageCount - 1, page <= 2 ? 2 : page);

  if (start > end) { start = page; end = page; }

  if (start > 2) items.push('gap');
  for (let p = start; p <= end; p++) items.push(p);
  if (end < pageCount - 1) items.push('gap');
  items.push(pageCount);

  return items;
}

interface ILeadsPaginationProps {
  page: number;
  pageSize: number;
  total: number;
  /** Dims the bar while a page is being fetched, without collapsing the layout. */
  loading?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const LeadsPagination: FunctionComponent<ILeadsPaginationProps> = ({
  page, pageSize, total, loading = false, onPageChange, onPageSizeChange,
}) => {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pageCount);
  const firstRow = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const lastRow = Math.min(total, current * pageSize);

  const go = (next: number) => {
    if (next < 1 || next > pageCount || next === current) return;
    onPageChange(next);
  };

  return (
    <PaginationBar $loading={loading}>
      <LeftGroup>
        <PageSizeWrap>
          <PageSizeSelect
            value={pageSize}
            onChange={e => onPageSizeChange(Number(e.target.value))}
            aria-label="Leads per page"
          >
            {LEADS_PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>{size} per page</option>
            ))}
          </PageSizeSelect>
          <PageSizeChevron aria-hidden><SvgFragment type="arrowDown" /></PageSizeChevron>
        </PageSizeWrap>
        <Divider aria-hidden />
        <RangeText>{firstRow}–{lastRow} of {total}</RangeText>
      </LeftGroup>

      {/* Centre track. Rendered even with one page so the grid keeps its shape. */}
      {pageCount > 1 ? (
        <PageButtons>
          <StepBtn
            type="button"
            onClick={() => go(current - 1)}
            disabled={current === 1}
            aria-label="Previous page"
          >
            <SvgFragment type="arrowLeft" />
          </StepBtn>
          <StepSpacer />
          {leadsPageItems(current, pageCount).map((item, i) => (
            item === 'gap'
              ? <Gap key={`gap-${i}`} aria-hidden>…</Gap>
              : (
                <PageBtn
                  key={item}
                  type="button"
                  $active={item === current}
                  onClick={() => go(item)}
                  aria-label={`Page ${item}`}
                  aria-current={item === current ? 'page' : undefined}
                >
                  {item}
                </PageBtn>
              )
          ))}
          <StepSpacer />
          <StepBtn
            type="button"
            onClick={() => go(current + 1)}
            disabled={current === pageCount}
            aria-label="Next page"
          >
            <SvgFragment type="arrowRight" />
          </StepBtn>
        </PageButtons>
      ) : <span />}

      {/* Right track: empty, it exists to balance the left one so the centre is centred. */}
      <span />
    </PaginationBar>
  );
};

// Three tracks, so the page buttons sit in the true centre of the bar rather than
// drifting with the width of the "1–20 of 500" label on the left.
const PaginationBar = styled.div<{ $loading: boolean }>`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  /* No rule above the bar: the controls carry their own edges, and the extra line only
     boxed the strip in. */
  padding: 12px 4px 4px;
  opacity: ${({ $loading }) => ($loading ? 0.5 : 1)};
  transition: opacity 0.15s ease;

  ${campaignMobile`
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 8px;
  `}
`;

const LeftGroup = styled.div`display: flex; align-items: center; gap: 12px;`;

const PageSizeWrap = styled.div`position: relative; display: inline-flex; align-items: center;`;

// The page-size control is the one interactive thing in the bar, so it carries the
// primary colour — the counts and page numbers around it stay neutral.
const PageSizeSelect = styled.select`
  appearance: none;
  height: 36px; padding: 0 32px 0 14px;
  border: 1.5px solid #dde3ee; border-radius: 10px; background: ${styles.colors.white}; cursor: pointer;
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: ${styles.colors.primary600};
  &:hover  { border-color: ${styles.colors.primary200}; }
  &:focus  { outline: none; border-color: ${styles.colors.primary600}; }
`;

const PageSizeChevron = styled.span`
  position: absolute; right: 9px; top: 50%; transform: translateY(-50%);
  display: inline-flex; pointer-events: none;
  .svg  { width: 14px; height: 14px; display: block; }
  .path { fill: ${styles.colors.primary600}; }
`;

const Divider = styled.span`
  width: 1px; height: 18px; background: ${styles.colors.black200};
  ${campaignMobile`display: none;`}
`;

const RangeText = styled.span`
  font-family: NunitoSans; font-size: 13px; color: ${styles.colors.black300};
  ${campaignMobile`display: none;`}
`;

const PageButtons = styled.div`display: flex; align-items: center; gap: 2px;`;

const pageBtnBase = `
  min-width: 32px; height: 32px; padding: 0 6px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 8px; cursor: pointer;
  font-family: NunitoSans; font-size: 13px; font-weight: 600;
  transition: background 0.14s, border-color 0.14s, color 0.14s;
`;

const PageBtn = styled.button<{ $active: boolean }>`
  ${pageBtnBase}
  border: 1px solid ${({ $active }) => ($active ? styles.colors.primary600 : 'transparent')};
  background: ${({ $active }) => ($active ? styles.colors.primary100 : 'transparent')};
  color: ${({ $active }) => ($active ? styles.colors.primary600 : styles.colors.black300)};
  font-weight: ${({ $active }) => ($active ? 700 : 600)};
  &:hover { background: ${({ $active }) => ($active ? styles.colors.primary100 : '#f4f6fb')}; }
`;

// Bordered, so prev/next read as controls while the numbers stay plain text.
const StepBtn = styled.button`
  ${pageBtnBase}
  border: 1px solid ${styles.colors.black200}; background: white;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  .svg  { width: 16px; height: 16px; display: block; }
  .path { fill: ${styles.colors.black300}; }
  &:hover:not(:disabled) {
    border-color: ${styles.colors.primary200};
    .path { fill: ${styles.colors.primary600}; }
  }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const Gap = styled.span`
  ${pageBtnBase}
  min-width: 20px; color: ${styles.colors.black300}; cursor: default;
`;

// Both step buttons sit slightly away from the numbers so the group reads as
// "‹ [pages] ›" rather than one undifferentiated strip.
const StepSpacer = styled.span`display: inline-block; width: 4px;`;

export default LeadsPagination;
